import { create } from "zustand";
import {
  createSpace as apiCreateSpace,
  fetchCurrentPlan,
  fetchMyOrgInvitations,
  fetchOrgs,
  fetchOrgSpaces,
  type CurrentPlan,
  type MyOrgInvitation,
  type Org,
  type Space,
} from "@/lib/api/organizations";

const ACTIVE_ORG_KEY = "mycorr.activeOrgId";
const COLLAPSED_KEY = "mycorr.collapsedSpaces";
const CREATE_SPACE_KEY = "mycorr.createSpaceByOrg";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

/// One picker row: the space plus its ancestor names, root-first. The picker
/// renders ancestors muted and the leaf bright ("Product Analytics / Data
/// Quality / Q3 Cleanup"), per the approved mocks — a flat list, never an
/// indented tree.
export interface SpacePath {
  space: Space;
  ancestors: string[];
}

/// Depth-first flattening (children under their parent, names sorted) so the
/// rail and picker agree on order. Call this inside a `useMemo` keyed on
/// `spaces` — NEVER from a zustand selector: it allocates a new array every
/// call, and the store compares with `Object.is`, so a selector returning it
/// re-renders forever (React #185). Cycles cannot exist (the server refuses
/// cross-org parents and a space cannot parent itself), but the visited set
/// keeps a corrupt row from hanging the UI.
/// The WORKING view: everything except what governance alone reaches (D3/D13
/// metadata). The full list stays in the store — org settings ▸ Spaces governs
/// it, the audit log names rows from it, and a governor still reaches any space
/// at `/spaces/:id` to un-seal it. Allocates, so the `flattenSpaceTree` rule
/// applies: inside a `useMemo`, never from a selector.
/// What the working surfaces draw: no governed-only rows, and no space inside
/// its deletion window — that one lives in the Trash, and its whole subtree
/// went with it (D53), so nothing live is left under it to re-root.
export function visibleSpaces(spaces: Space[]): Space[] {
  return spaces.filter((s) => !s.governed_only && !s.deletion_scheduled_at);
}

/// The path to one space, root-first and ending with the space itself — what
/// a breadcrumb walks back up. Empty when the id is not in `spaces` (a space
/// deleted or narrowed away under an open filter). The visited set is the
/// same corrupt-row guard `flattenSpaceTree` carries.
export function spaceTrail(spaces: Space[], spaceId: string): Space[] {
  const byId = new Map(spaces.map((sp) => [sp.space_id, sp]));
  const trail: Space[] = [];
  const visited = new Set<string>();
  let current = byId.get(spaceId);
  while (current && !visited.has(current.space_id)) {
    visited.add(current.space_id);
    trail.unshift(current);
    current = current.parent_space_id
      ? byId.get(current.parent_space_id)
      : undefined;
  }
  return trail;
}

export function flattenSpaceTree(spaces: Space[]): SpacePath[] {
  const byParent = new Map<string | null, Space[]>();
  for (const sp of spaces) {
    const key = sp.parent_space_id ?? null;
    const bucket = byParent.get(key) ?? [];
    bucket.push(sp);
    byParent.set(key, bucket);
  }
  for (const bucket of byParent.values()) {
    bucket.sort((a, b) => a.name.localeCompare(b.name));
  }
  const out: SpacePath[] = [];
  const visited = new Set<string>();
  const walk = (parent: string | null, ancestors: string[]) => {
    for (const space of byParent.get(parent) ?? []) {
      if (visited.has(space.space_id)) continue;
      visited.add(space.space_id);
      out.push({ space, ancestors });
      walk(space.space_id, [...ancestors, space.name]);
    }
  };
  walk(null, []);
  // Orphans (visible child of an invisible parent): surface at top level
  // rather than dropping them.
  for (const sp of spaces) {
    if (!visited.has(sp.space_id)) {
      visited.add(sp.space_id);
      out.push({ space: sp, ancestors: [] });
    }
  }
  return out;
}

interface OrgContextState {
  orgs: Org[];
  /// The active context. Every models/search request carries it (S6c
  /// narrowing); direct shares and catalog stay visible regardless (D18/D20).
  activeOrgId: string | null;
  spaces: Space[];
  spacesLoading: boolean;
  /// Rail collapse state, persisted — space_id -> collapsed.
  collapsed: Record<string, boolean>;
  /// Per-org "create in" selection, persisted — org_id -> space_id.
  createSpaceByOrg: Record<string, string>;
  /// Rail filter for the overview list. Session-only, cleared on org switch.
  selectedSpaceId: string | null;
  /// The active org's plan, for surfaces that need its limits (the new-org
  /// welcome strip). Null while loading or when the org has no recorded
  /// period. The switcher's badge does NOT read this — it labels every row
  /// from `Org.plan_name`, so the trigger and the menu agree.
  plan: CurrentPlan | null;

  /// Bumped after an image upload/removal so every OrgMark re-fetches.
  imageVersion: number;

  /// Invitations awaiting THIS user's answer (D43). Never merged into `orgs`
  /// — an unaccepted org must not become switchable.
  pendingInvites: MyOrgInvitation[];
  /// Whether `pendingInvites` has ever been fetched. An empty list means
  /// "nothing pending" only once this is true; before that it means "unknown",
  /// and callers must not read absence as a settled invitation.
  invitesLoaded: boolean;

  init: () => Promise<void>;
  refreshInvites: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  adoptOrg: (org: Org) => void;
  /// Re-fetches the org list (rename, image flag) without touching the
  /// active context, spaces or plan.
  refreshOrgs: () => Promise<void>;
  bumpImageVersion: () => void;
  refreshSpaces: () => Promise<void>;
  toggleCollapsed: (spaceId: string) => void;
  setCreateSpace: (spaceId: string) => void;
  setSelectedSpace: (spaceId: string | null) => void;
  createSpace: (
    name: string,
    options?: {
      parentSpaceId?: string;
      color?: string;
      sealed?: boolean;
      /// `open` | `restricted` | `sealed` (D39); wins over `sealed`.
      classification?: string;
      managers?: string[];
    },
  ) => Promise<Space | null>;

  activeOrg: () => Org | undefined;
  spaceById: (spaceId: string | null | undefined) => Space | undefined;
  /// The effective "create in" target: the persisted pick if it still exists,
  /// else the first space in tree order (the starter space for a fresh org).
  createSpaceId: () => string | null;
}

export const useOrgContextStore = create<OrgContextState>((set, get) => ({
  orgs: [],
  activeOrgId: null,
  spaces: [],
  spacesLoading: false,
  collapsed: readJson<Record<string, boolean>>(COLLAPSED_KEY, {}),
  createSpaceByOrg: readJson<Record<string, string>>(CREATE_SPACE_KEY, {}),
  selectedSpaceId: null,
  plan: null,
  imageVersion: 0,
  pendingInvites: [],
  invitesLoaded: false,

  init: async () => {
    void get().refreshInvites();
    const orgs = await fetchOrgs();
    const stored = localStorage.getItem(ACTIVE_ORG_KEY);
    const active =
      orgs.find((o) => o.org_id === stored)?.org_id ??
      orgs.find((o) => o.is_personal)?.org_id ??
      orgs[0]?.org_id ??
      null;
    set({ orgs, activeOrgId: active });
    if (active) {
      localStorage.setItem(ACTIVE_ORG_KEY, active);
      // Plans key on the org (S4a), so the label follows the active context.
      const [plan] = await Promise.all([
        fetchCurrentPlan(active),
        get().refreshSpaces(),
      ]);
      if (get().activeOrgId === active) set({ plan });
    }
  },

  refreshOrgs: async () => {
    const orgs = await fetchOrgs();
    // `fetchOrgs` answers [] on failure too, and every account has at least a
    // personal org, so an empty list means the read failed — keep what we had.
    if (orgs.length === 0) return;
    const activeId = get().activeOrgId;
    const stillAMember = orgs.some((o) => o.org_id === activeId);
    set({ orgs });
    // Removed from the org we were standing in. The switcher renders nothing
    // for an active org it cannot find and every scoped request 403s, so fall
    // back rather than leave the UI in a context that no longer exists.
    if (activeId && !stillAMember) {
      const fallback = orgs.find((o) => o.is_personal) ?? orgs[0];
      if (fallback) await get().switchOrg(fallback.org_id);
    }
  },

  refreshInvites: async () => {
    const pendingInvites = await fetchMyOrgInvitations();
    set({ pendingInvites, invitesLoaded: true });
  },

  /// Put an org into the list before a refresh has proven it. The switcher
  /// renders nothing when the active org is absent from `orgs`, so a create
  /// whose refresh lost the read-after-write race would blank the sidebar and
  /// read as "it never switched". The next refresh supersedes this row.
  adoptOrg: (org) =>
    set((s) =>
      s.orgs.some((o) => o.org_id === org.org_id)
        ? s
        : { orgs: [...s.orgs, org] },
    ),

  bumpImageVersion: () => set({ imageVersion: get().imageVersion + 1 }),

  switchOrg: async (orgId) => {
    if (orgId === get().activeOrgId) return;
    localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    set({ activeOrgId: orgId, spaces: [], selectedSpaceId: null, plan: null });
    const [plan] = await Promise.all([
      fetchCurrentPlan(orgId),
      get().refreshSpaces(),
    ]);
    // Ignore a slow response that lost a race with another switch.
    if (get().activeOrgId === orgId) set({ plan });
  },

  refreshSpaces: async () => {
    const orgId = get().activeOrgId;
    if (!orgId) return;
    set({ spacesLoading: true });
    const spaces = await fetchOrgSpaces(orgId);
    // Ignore a slow response that lost a race with an org switch.
    if (get().activeOrgId === orgId) {
      // A refresh can be the first news that access went away, so the rail
      // filter has to let go of a space that is no longer in the list —
      // otherwise the overview stays scoped to something the user cannot see.
      const selected = spaces.find((s) => s.space_id === get().selectedSpaceId);
      const selectedSpaceId =
        selected && !selected.deletion_scheduled_at ? selected.space_id : null;
      set({ spaces, spacesLoading: false, selectedSpaceId });
    }
  },

  toggleCollapsed: (spaceId) => {
    const collapsed = {
      ...get().collapsed,
      [spaceId]: !get().collapsed[spaceId],
    };
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed));
    set({ collapsed });
  },

  // Scoping the overview to a space also makes it the create-in target: the
  // approved split control creates in the last-used space, and the space you
  // just scoped to is the one you are working in. Deselecting leaves the target
  // where it is — clearing a filter is not a statement about where to create.
  setSelectedSpace: (spaceId) => {
    set({ selectedSpaceId: spaceId });
    if (spaceId) get().setCreateSpace(spaceId);
  },

  setCreateSpace: (spaceId) => {
    const orgId = get().activeOrgId;
    if (!orgId) return;
    const createSpaceByOrg = { ...get().createSpaceByOrg, [orgId]: spaceId };
    localStorage.setItem(CREATE_SPACE_KEY, JSON.stringify(createSpaceByOrg));
    set({ createSpaceByOrg });
  },

  createSpace: async (name, options) => {
    const orgId = get().activeOrgId;
    if (!orgId) return null;
    const created = await apiCreateSpace(orgId, name, options);
    if (created) {
      await get().refreshSpaces();
    }
    return created;
  },

  activeOrg: () => get().orgs.find((o) => o.org_id === get().activeOrgId),

  spaceById: (spaceId) =>
    spaceId ? get().spaces.find((s) => s.space_id === spaceId) : undefined,

  createSpaceId: () => {
    const { activeOrgId, createSpaceByOrg } = get();
    if (!activeOrgId) return null;
    const spaces = visibleSpaces(get().spaces);
    const stored = createSpaceByOrg[activeOrgId];
    if (stored && spaces.some((s) => s.space_id === stored)) return stored;
    return flattenSpaceTree(spaces)[0]?.space.space_id ?? null;
  },
}));

/// The pickable swatch vocabulary (create dialog + settings header) — also
/// the fallback wheel for spaces with no stored color.
export const SPACE_PALETTE = [
  "sky",
  "ice",
  "frost",
  "dawn",
  "aurora",
  "glow",
  "ember",
  "berry",
];

export function spaceColorToken(spaceId: string): string {
  let hash = 0;
  for (let i = 0; i < spaceId.length; i++) {
    hash = (hash * 31 + spaceId.charCodeAt(i)) >>> 0;
  }
  return SPACE_PALETTE[hash % SPACE_PALETTE.length];
}

/// The space's dot color: the stored pick when there is one, else the stable
/// id-derived slot (what every pre-color space has always rendered).
export function spaceColor(space: Pick<Space, "space_id" | "color">): string {
  return space.color || spaceColorToken(space.space_id);
}
